import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import ProposalDeck from "@/components/ProposalDeck";
import { ProposalProvider } from "@/lib/proposalContext";

/**
 * Authed proposal print view. Fetches the quote + settings behind auth,
 * then hands them to the shared <ProposalDeck>. Adds a small print
 * toolbar and auto-triggers window.print() 500ms after data lands so
 * the browser Save-as-PDF flow works with a single click.
 *
 * The anonymous share-link route (/proposal/:token) is the sibling
 * `PublicProposal` component — same deck, different data source.
 */
export default function QuotePrint() {
  const { id } = useParams();
  const [quote, setQuote] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([api.get(`/quotes/${id}`), api.get("/settings")])
      .then(([q, s]) => {
        setQuote(q.data);
        setSettings(s.data);
      })
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    if (!loading && quote) {
      const t = setTimeout(() => window.print(), 500);
      return () => clearTimeout(t);
    }
  }, [loading, quote]);

  if (loading) return <div className="p-10 text-gray-600">Loading…</div>;
  if (!quote) return <div className="p-10 text-red-600">Quote not found</div>;

  return (
    <ProposalProvider mode="authed">
      <div className="no-print-toolbar bg-gray-100 py-3 flex justify-center gap-2 sticky top-0 z-50">
        <button
          onClick={() => window.print()}
          className="px-4 py-2 rounded bg-[#1E9CD7] text-white text-sm font-semibold"
          data-testid="print-now-btn"
        >
          Print / Save as PDF
        </button>
        <div className="text-xs text-gray-500 self-center">
          Ctrl / Cmd + P to open the print dialog manually
        </div>
      </div>
      <ProposalDeck quote={quote} settings={settings} />
    </ProposalProvider>
  );
}
